<svelte:options runes={true} />

<script lang="ts">
  import { deleteSurface, makeSurface } from '@ck';
  import { AdaptiveColor } from '@color';
  import type { CommandManager } from '@commands';
  import * as Commands from '@commands';
  import {
    CommandContext,
    CommandId,
    Cursor,
    DragType,
    type CommandState,
    type ToolManager
  } from '@commands';
  import {
    Doc,
    LayoutOrientation,
    Layouter,
    drawDoc,
    findElementsIntersectingRect,
    findGroupAt,
    findHoveredElementAt,
    findHoveredGroupAt
  } from '@doc';
  import {
    HAlignment,
    Point,
    Rect,
    VAlignment,
    lineSegment,
    point,
    rect,
    size
  } from '@geom';
  import type { EdgeEndpoint, NID, Nodes } from '@graph';
  import { EdgeDirection, Elements } from '@graph';
  import { LayoutConstants, SpatialIndex } from '@layout';
  import type { KeyManager } from '@reactive';
  import {
    TitleEditorState,
    UndoManager,
    localeManager,
    prefs,
    type UndoState
  } from '@reactive';
  import { canvasKit, loadResources } from '@resources';
  import { StringId, iString, loremify, trimLines } from '@strings';
  import type { TransitionManager } from '@ui';
  import { getElementBounds } from '@ui';
  import {
    assertDefined,
    clamp,
    expectDefined,
    fixedNumber,
    lerpMap,
    reactTo
  } from '@utils';
  import { ArrowType, CKVisual, connectArrowEquals, type ConnectArrow } from '@visual';
  import type { Canvas, Surface } from 'canvaskit';
  import { untrack } from 'svelte';

  //
  // NOTE:
  // - The term "doc" refers to the coordinate space of the document (PDF
  //   points). This is the space in which the document is laid out.
  // - The term "element" refers to the coordinate space of the canvas element.
  //   This is the space in which pointer events are received.
  //
  // The variables mediating between these two spaces are `pan` and `zoom`. Note
  // that these are Svelte reactive variables. Which means that when they are
  // updated, the canvas is redrawn automatically-- you don't need to call
  // `requestFrame`.
  //

  // #region Props

  let {
    doc = $bindable(),
    selection = $bindable(),
    lastSelection = $bindable(),
    selectedUnitClass,
    selectedGroupClass,
    spatialIndex = $bindable(),
    zoom = $bindable(),
    targetZoom = $bindable(),
    minZoom,
    maxZoom,
    pan = $bindable(),
    targetPan = $bindable(),
    entryPoints = $bindable(),
    layouter,
    toolManager,
    undoManager,
    commandManager,
    keyManager,
    transitionManager = $bindable(),
    assignUp,
    titleEditorState = $bindable(),
    isModal
  }: {
    doc: Doc;
    selection: Elements;
    lastSelection: Elements;
    selectedUnitClass: string | undefined;
    selectedGroupClass: string | undefined;
    spatialIndex: SpatialIndex | undefined;
    zoom: number; // The zoom level of the document, clamped to [minZoom, maxZoom].
    targetZoom: number | undefined; // The zoom level to animate to.
    minZoom: number; // Typically 0.1.
    maxZoom: number; // Typically 2.
    pan: Point; // 0,0 means the origin of the document is at the top-left corner of the canvas.
    targetPan: Point | undefined; // The pan to animate to.
    entryPoints: Map<NID, Point> | undefined;
    layouter?: Layouter;
    toolManager: ToolManager;
    undoManager: UndoManager;
    commandManager: CommandManager;
    keyManager: KeyManager;
    transitionManager?: TransitionManager;
    titleEditorState: TitleEditorState;
    isModal: boolean;
    assignUp: (element: HTMLElement, getCommandContext: () => CommandContext) => void;
  } = $props();

  // #endregion

  // #region Attributes

  undoManager.getUndoState = getUndoState;
  undoManager.setUndoState = setUndoState;

  let isDestroyed = false;
  let memoryLogInterval: number | undefined;
  let surface: Surface | undefined;
  let frameRequested = false;

  let resizeObserver: ResizeObserver | undefined;

  let isPointerDown = false;

  let dragType: DragType | undefined;

  let shiftAtStartDrag = false;
  let elementsSelectedAtStartDrag: Elements | undefined;
  let elementsHoveredAtStartDrag: Elements | undefined;
  let endpointHoveredAtStartDrag = $state<EdgeEndpoint | undefined>(undefined);

  let lastZoom: number | undefined;
  let lastPan: Point | undefined;

  let element: HTMLCanvasElement;
  export function getElement(): HTMLCanvasElement {
    return element;
  }

  //
  // Element space units
  //

  // The current pointer position in element space.
  let elemPointerPosition = $state<Point | undefined>();
  // The last pointer position in element space while the pointer was pressed down.
  let elemLastPointerDownPosition: Point | undefined;
  let hoveredAtPointerDown: Elements | undefined;
  let endpointHoveredAtPointerDown: EdgeEndpoint | undefined;
  // The pointer position when the pointer was pressed down, in element space.
  let elemPointerDownPosition = $state<Point | undefined>();
  // The pointer position when the pointer was released, in element space.
  let elemPointerUpPosition: Point | undefined;

  //
  // Doc space units
  //

  // The current bounds of the selection rectangle in doc space.
  let docSelectBounds = $state<Rect | undefined>();
  let docLastSelectBounds: Rect | undefined;
  // The current connection arrow being dragged out in doc space.
  let docConnectArrow = $state<ConnectArrow | undefined>();
  let docLastConnectArrow: ConnectArrow | undefined;
  // The current node creation indicators in doc space.
  let docNodeCreationIndicator1 = $state<Rect | undefined>();
  let docLastNodeCreationIndicator1: Rect | undefined;
  let docNodeCreationIndicator2 = $state<Rect | undefined>();
  let docLastNodeCreationIndicator2: Rect | undefined;

  let needsUpdateCursor = $state(false);

  // Client space units
  const CLICK_THRESHOLD = 5;
  const DRAG_THRESHOLD = 3;

  const AUTOSCROLL_THRESHOLD = 50; // Pixels from canvas edge
  const AUTOSCROLL_MAX_SPEED = 500; // Pixels per second
  // Set when autoscroll is active
  let autoscrollIntervalId: number | undefined;
  // Rate of autoscroll in pixels per second
  let autoscrollVector = Point.ZERO;
  let lastAutoscrollTimestamp: number;

  // This is a preference the user could set.
  const INERTIAL_SCROLL_START_VELOCITY = 0.4;
  const INERTIAL_SCROLL_END_VELOCITY = 0.01;
  const INERTIAL_SCROLL_DECAY = 0.01;
  let inertiaPoints: [Point, number][] = [];
  let inertialPointerVelocity = Point.ZERO;
  let inertialScrollVector = Point.ZERO;
  let inertialScrollIntervalId: number | undefined;
  let lastInertialScrollTimestamp: number;

  // #endregion

  // #region Reactive

  let isAnimating = $derived(transitionManager !== undefined);

  let lastHovered = Elements.make();
  let hovered = $derived.by<Elements>(() => {
    reactTo(doc);
    if (isModal || isAnimating || !docPointerPosition || !spatialIndex) {
      return Elements.make();
    }
    return findHoveredElementAt(doc, docPointerPosition, spatialIndex);
  });

  let hoveredEndpoint = $derived.by<EdgeEndpoint | undefined>(() => {
    if (docPointerPosition === undefined) {
      return undefined;
    }
    const hoveredEdge = hovered.anyEdge;
    if (hoveredEdge === undefined) {
      return undefined;
    } else {
      const edgeLayout = doc.edgeLayout(hoveredEdge);
      if (
        docPointerPosition.distanceTo(edgeLayout.path.pointAt(0)) <=
        LayoutConstants.ENDPOINT_HIGHTLIGHT_RADIUS
      ) {
        return { edge: hoveredEdge, direction: EdgeDirection.TAIL };
      } else if (
        docPointerPosition.distanceTo(edgeLayout.path.pointAt(1)) <=
        LayoutConstants.ENDPOINT_HIGHTLIGHT_RADIUS
      ) {
        return { edge: hoveredEdge, direction: EdgeDirection.HEAD };
      } else {
        return undefined;
      }
    }
  });

  // The current pointer position in doc space.
  let docPointerPosition: Point | undefined = $derived(
    toDocPosition(elemPointerPosition, pan, zoom)
  );
  // The pointer position when the pointer was pressed down in doc space.
  let docPointerDownPosition: Point | undefined = $derived(
    toDocPosition(elemPointerDownPosition, pan, zoom)
  );

  $effect(() => {
    // When the document changes, request a frame to redraw the document.
    reactTo(doc);
    requestFrame('$effect(doc)');
  });

  $effect(() => {
    // When the selection changes, request a frame to redraw the document.
    reactTo(selection);
    requestFrame('$effect(selection)');
  });

  $effect(() => {
    // When the hovered element changes, request a frame to redraw the document.
    if (!hovered.equals(lastHovered)) {
      lastHovered = hovered;
      requestFrame('$effect(hovered)');
    }
  });

  $effect(() => {
    // When the hovered endpoint changes, request a frame to redraw the document.
    reactTo(hoveredEndpoint, endpointHoveredAtStartDrag);
    requestFrame('$effect(hoveredEndpoint)');
  });

  $effect(() => {
    // When the tool changes, request a frame to redraw the document.
    reactTo(toolManager.activeTool);
    setNeedsUpdateCursor('$effect(toolManager.activeTool)');
    requestFrame('$effect(toolManager.activeTool)');
  });

  $effect(() => {
    // When the docNodeCreationIndicator1 changes, request a frame to redraw the document.
    if (Rect.equals(docNodeCreationIndicator1, docLastNodeCreationIndicator1)) {
      return;
    }
    docLastNodeCreationIndicator1 = docNodeCreationIndicator1;
    requestFrame('$effect(docNodeCreationIndicator1)');
  });

  $effect(() => {
    // When the docNodeCreationIndicator2 changes, request a frame to redraw the document.
    if (Rect.equals(docNodeCreationIndicator2, docLastNodeCreationIndicator2)) {
      return;
    }
    docLastNodeCreationIndicator2 = docNodeCreationIndicator2;
    requestFrame('$effect(docNodeCreationIndicator2)');
  });

  $effect(() => {
    // When the docSelectBounds changes, request a frame to redraw the document.
    if (Rect.equals(docSelectBounds, docLastSelectBounds)) {
      return;
    }
    docLastSelectBounds = docSelectBounds;
    requestFrame('$effect(docSelectBounds)');
  });

  $effect(() => {
    // When the docConnectArrow changes, request a frame to redraw the document.
    if (connectArrowEquals(docConnectArrow, docLastConnectArrow)) {
      return;
    }
    docLastConnectArrow = docConnectArrow;
    requestFrame('$effect(docConnectArrow)');
  });

  $effect(() => {
    // When we need to update the cursor, update the cursor.
    if (needsUpdateCursor) {
      updateCursor('$effect(needsUpdateCursor)');
    }
  });

  $effect(() => {
    // When the modifier keys change, note that the cursor needs to be updated.
    reactTo(keyManager.change);
    setNeedsUpdateCursor(`$effect(keyManager.change) ${keyManager.debugString}`);
  });

  $effect(() => {
    // When the color mode changes, request a frame to redraw the document.
    reactTo(prefs.colorMode);
    requestFrame('$effect(prefs.colorMode)');
  });

  $effect(() => {
    // When zoom or pan changes, request a frame to redraw the document.
    if (
      lastZoom === undefined ||
      lastPan === undefined ||
      zoom !== lastZoom ||
      !pan.equals(lastPan)
    ) {
      // console.log('$effect(zoom, pan)');
      lastZoom = zoom;
      lastPan = pan;
      updateCursor('$effect(zoom, pan)');
      requestFrame('$effect(zoom, pan)');
    }
  });

  $effect(() => {
    loadResources().then(
      () => {
        // startLoggingMemory();

        adjustSurfaceSize('onMount');

        resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            if (entry.target === element) {
              adjustSurfaceSize('resizeObserver');
            }
          }
        });
        resizeObserver.observe(element);
        assignUp(element, commandContext);

        addObservers();
        bindCommands();

        return () => {
          removeObservers();
        };
      },
      (reason: unknown) => {
        console.error(reason);
      }
    );

    return () => {
      stopAutoscroll();

      if (surface) {
        deleteSurface(surface);
        // surface = undefined;
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      isDestroyed = true;

      if (memoryLogInterval) {
        stopLoggingMemory();
      }
    };
  });

  $effect(() => {
    // When there is a modal interface, disable the key manager.
    if (isModal) {
      keyManager.disable();
    } else {
      keyManager.enable();
    }
  });

  // #endregion

  // #region Drawing

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function adjustSurfaceSize(reason: string) {
    // console.log(`${(new Date()).getTime()} adjustSurfaceSize: ${reason}`);

    element.width = element.clientWidth;
    element.height = element.clientHeight;

    if (surface) {
      const width = surface.width();
      const height = surface.height();
      if (width === element.width && height === element.height) {
        return;
      }
    }

    const oldSurface = surface;

    surface = makeSurface(element);

    requestFrame('adjustSurfaceSize');

    if (oldSurface) {
      deleteSurface(oldSurface);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function requestFrame(reason: string) {
    // Calls to `requestAnimationFrame` are *not* idempotent: each call
    // schedules a new frame, even if one is already scheduled.
    //
    // Here we make `requestFrame` idempotent by checking if a frame is already
    // requested.

    // console.log(`${(new Date()).getTime()} requestFrame: ${reason}`);

    if (frameRequested) {
      return;
    }

    if (surface === undefined) {
      return;
    }
    frameRequested = true;
    // console.log(`${(new Date()).getTime()} frameRequested = true`);
    surface.requestAnimationFrame(drawFrame);
    // setNeedsUpdateCursor('requestFrame');
  }

  function drawFrame(canvas: Canvas) {
    // console.log(`${(new Date()).getTime()} drawFrame`);
    // playBeep();

    const showPerformance = false;

    let start: DOMHighResTimeStamp | undefined;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (showPerformance) {
      start = performance.now();
    }

    if (isDestroyed) {
      return;
    }

    let docToDraw: Doc;
    let selectionToDraw: Elements;
    let panToDraw: Point;
    let zoomToDraw: number;
    let transitionT: number | undefined;
    let creationIndicatorToDraw: Rect | undefined;
    if (transitionManager !== undefined) {
      const { doc, selection, zoom, pan, t } = transitionManager.get();
      docToDraw = doc;
      selectionToDraw = selection;
      panToDraw = pan;
      zoomToDraw = zoom;
      transitionT = t;
      if (docNodeCreationIndicator1 !== undefined && elemPointerPosition !== undefined) {
        const center = expectDefined(
          toDocPosition(elemPointerPosition, panToDraw, zoomToDraw)
        );
        creationIndicatorToDraw = docNodeCreationIndicator1.settingCenter(center);
      }
      if (t === 1) {
        transitionManager = undefined;
        spatialIndex = doc.makeSpatialIndex();
      }
    } else {
      docToDraw = doc;
      selectionToDraw = selection;
      panToDraw = pan;
      zoomToDraw = zoom;
      creationIndicatorToDraw = docNodeCreationIndicator1;
    }

    const highlightedEndpoint =
      toolManager.activeTool == CommandId.TOOL_ARRANGE
        ? (hoveredEndpoint ?? endpointHoveredAtStartDrag)
        : undefined;

    const visual = new CKVisual(
      prefs.colorMode,
      expectDefined(layouter?.cache),
      canvas,
      expectDefined(surface)
    );

    drawDoc(docToDraw, visual, {
      zoom: zoomToDraw,
      pan: panToDraw,
      selection: selectionToDraw,
      hovered,
      interactionOpts: {
        selectBounds: docSelectBounds,
        connectArrow: docConnectArrow,
        nodeCreationIndicator1: creationIndicatorToDraw,
        nodeCreationIndicator2: docNodeCreationIndicator2,
        highlightedEndpoint
      },
      debugOpts: {
        // markerPoint: docPointerDownPosition,
        // markerRect: doc.bounds,
      }
    });
    // clientPositionToDocPosition();

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (showPerformance) {
      const end = performance.now();
      const seconds = (end - expectDefined(start)) / 1000;
      const framesPerSecond = 1 / seconds;
      console.log(
        `Frame time: ${fixedNumber(seconds, 2)} seconds. (${fixedNumber(framesPerSecond, 2)} fps)`
      );
    }

    frameRequested = false;

    if (transitionT !== undefined && transitionT < 1) {
      requestFrame('transition in progress');
    }
  }

  // #endregion

  // #region Memory

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function startLoggingMemory() {
    memoryLogInterval = setInterval(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const bytes: number = (canvasKit as any).HEAP8.length;
      const megabytes = fixedNumber(bytes / 1024 / 1024, 2);
      console.log(`Memory usage: ${megabytes} MB`);
    }, 10000) as unknown as number;
  }

  function stopLoggingMemory() {
    if (memoryLogInterval) {
      clearInterval(memoryLogInterval);
    }
  }

  // #endregion

  // #region Cursor

  // https://www.w3schools.com/cssref/pr_class_cursor.php

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function updateCursor(reason: string) {
    // console.log(`${(new Date()).getTime()} updateCursor: ${reason}`);

    let cursor = Cursor.DEFAULT;

    switch (toolManager.activeTool) {
      case CommandId.TOOL_CREATE:
        if (hovered.anyEdge) {
          cursor = Cursor.CROSSHAIR;
        } else if (dragType === DragType.CONNECT) {
          cursor = Cursor.HIDDEN;
        } else {
          cursor = showCreateNodeIndicatorOver(hovered.anyNode)
            ? Cursor.HIDDEN
            : Cursor.CONNECT;
        }
        break;
      case CommandId.TOOL_SELECT:
        cursor = Cursor.SELECT;
        break;
      case CommandId.TOOL_ARRANGE:
        if (dragType === DragType.ARRANGE) {
          cursor = Cursor.HIDDEN;
        } else if (
          hoveredEndpoint !== undefined ||
          endpointHoveredAtStartDrag !== undefined
        ) {
          cursor = Cursor.CROSSHAIR;
        } else {
          cursor = Cursor.ARRANGE;
        }
        break;
      case CommandId.TOOL_PAN:
        cursor = isPointerDown ? Cursor.PANNING : Cursor.PAN;
        break;
      default:
        break;
    }

    needsUpdateCursor = false;
    element.style.setProperty('--cursor-style', cursor);

    // console.log(`Update cursor, tool: ${toolManager.activeTool}, cursor: ${cursor}`);

    updateNodeCreationIndicators();
  }

  // Empty canvas, groups, and edges are OK.
  function showCreateNodeIndicatorOver(node: NID | undefined): boolean {
    if (node === undefined) {
      return true;
    }
    if (doc.isGroup(node)) {
      return true;
    }
    return false;
  }

  function updateNodeCreationIndicators() {
    if (
      docPointerPosition === undefined ||
      toolManager.activeTool !== CommandId.TOOL_CREATE
    ) {
      docNodeCreationIndicator1 = undefined;
      docNodeCreationIndicator2 = undefined;
      return;
    }

    if (showCreateNodeIndicatorOver(hovered.anyNode)) {
      docNodeCreationIndicator1 = rect({
        center: docPointerPosition,
        size: size(50, 50)
      });
    } else {
      docNodeCreationIndicator1 = undefined;
    }

    if (
      dragType === DragType.CONNECT &&
      showCreateNodeIndicatorOver(expectDefined(elementsHoveredAtStartDrag).anyNode)
    ) {
      docNodeCreationIndicator2 = rect({
        center: expectDefined(docPointerDownPosition),
        size: size(50, 50)
      });
    } else {
      docNodeCreationIndicator2 = undefined;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function setNeedsUpdateCursor(reason: string) {
    // console.log(`setNeedsUpdateCursor ${reason}`);
    needsUpdateCursor = true;
  }

  // #endregion

  // #region Pointer

  function toDocPosition(
    elemPosition: Point | undefined,
    pan: Point,
    zoom: number
  ): Point | undefined {
    if (!elemPosition) {
      return undefined;
    }
    const elemBounds = getElementBounds(element, false);
    if (elemBounds === undefined) {
      return undefined;
    }
    const elemOffset = elemBounds.topLeft;
    const docRect = elemBounds
      .sub(elemOffset)
      .sub(pan)
      .mul(1 / zoom);
    return elemBounds.mapPointToRect(elemPosition, docRect);
  }

  function toElemPosition(docBounds: Rect, zeroTopLeft = true): Rect | undefined {
    const elemBounds = getElementBounds(element, zeroTopLeft);
    if (elemBounds === undefined) {
      return undefined;
    }
    const elemOffset = elemBounds.topLeft;

    const elemRect = docBounds.mul(zoom).add(pan).add(elemOffset);

    return elemRect;
  }

  function handlePointerEnterEvent(event: PointerEvent) {
    elemPointerPosition = point(event.clientX, event.clientY);
    handlePointerMove();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handlePointerLeaveEvent(event: PointerEvent) {
    elemPointerPosition = undefined;
    handlePointerMove();
  }

  function handlePointerMoveEvent(event: PointerEvent) {
    elemPointerPosition = point(event.clientX, event.clientY);
    handlePointerMove();
    updateAutoscrollForEvent(event);
  }

  function handlePointerMove() {
    setNeedsUpdateCursor('handlePointerMoveTo');

    if (elemPointerPosition === undefined) {
      return;
    }

    updateInertialPointerVelocity(elemPointerPosition);

    if (isPointerDown) {
      startDrag(elemPointerPosition);
      updateDrag(elemPointerPosition);
      elemLastPointerDownPosition = elemPointerPosition;
    }
  }

  function handlePointerDownEvent(event: PointerEvent) {
    isPointerDown = true;
    elemPointerDownPosition = point(event.clientX, event.clientY);
    elemLastPointerDownPosition = elemPointerDownPosition;
    hoveredAtPointerDown = hovered;
    endpointHoveredAtPointerDown = hoveredEndpoint;

    stopInertialScrolling();

    element.setPointerCapture(event.pointerId);
    updateCursor('handlePointerDown');
  }

  function handlePointerUpEvent(event: PointerEvent) {
    isPointerDown = false;
    elemLastPointerDownPosition = undefined;
    elemPointerUpPosition = point(event.clientX, event.clientY);

    if (isDragging()) {
      finishDrag();
    } else if (isClick()) {
      handleClick();
    }

    element.releasePointerCapture(event.pointerId);
  }

  function handlePointerCancelEvent(event: PointerEvent) {
    isPointerDown = false;
    elemLastPointerDownPosition = undefined;
    elemPointerUpPosition = undefined;

    endDrag();

    element.releasePointerCapture(event.pointerId);
  }

  function handlePointerOutEvent(event: PointerEvent) {
    isPointerDown = false;
    elemLastPointerDownPosition = undefined;
    elemPointerUpPosition = undefined;

    endDrag();

    element.releasePointerCapture(event.pointerId);
  }

  // #endregion

  // #region Drag

  function isDragging(): boolean {
    return dragType !== undefined;
  }

  function startDrag(elemCurrentPointerPosition: Point) {
    if (isDragging() || isAnimating) {
      return;
    }

    if (elemLastPointerDownPosition === undefined) {
      return;
    }

    const delta = elemCurrentPointerPosition.sub(elemLastPointerDownPosition);
    if (delta.magnitude > DRAG_THRESHOLD) {
      elementsSelectedAtStartDrag = selection;
      switch (toolManager.activeTool) {
        case CommandId.TOOL_CREATE:
          startConnectDrag();
          break;
        case CommandId.TOOL_SELECT:
          startSelectDrag();
          break;
        case CommandId.TOOL_ARRANGE:
          startArrangeDrag();
          break;
        case CommandId.TOOL_PAN:
          dragType = DragType.PAN;
          break;
      }
    }
  }

  function startConnectDrag() {
    const hovered = expectDefined(hoveredAtPointerDown);
    if (keyManager.shift) {
      // Edges are thin, so starting a drag from them needs to be precise.
      // We don't want to start a drag from the hovered element at the time
      // we detect the drag, because the user might have moved the pointer
      // slightly since then. Instead, we use the element that was hovered
      // when the pointer was pressed down.
      selection = selection.adding(hovered);
    } else {
      selection = hovered;
    }
    elementsHoveredAtStartDrag = hovered;
    dragType = DragType.CONNECT;
    startAutoscroll();
  }

  function startArrangeDrag() {
    const hovered = expectDefined(hoveredAtPointerDown);
    if (hovered.isEmpty) {
      return;
    }
    if (keyManager.shift) {
      selection = selection.adding(hovered);
    } else {
      selection = hovered;
    }
    endpointHoveredAtStartDrag = endpointHoveredAtPointerDown;
    dragType = DragType.ARRANGE;
    startAutoscroll();
  }

  function startSelectDrag() {
    dragType = DragType.SELECT;
    shiftAtStartDrag = keyManager.shift;
    startAutoscroll();
  }

  function updateDrag(elemCurrentPointerPosition: Point) {
    switch (dragType) {
      case DragType.CONNECT:
        updateConnectDrag();
        break;
      case DragType.SELECT:
        updateSelectDrag();
        break;
      case DragType.ARRANGE:
        updateArrangeDrag();
        break;
      case DragType.PAN:
        updatePanDrag(elemCurrentPointerPosition);
        break;
    }
  }

  function updateConnectDrag() {
    if (docPointerDownPosition === undefined || docPointerPosition === undefined) {
      return;
    }
    docConnectArrow = {
      line: lineSegment(docPointerDownPosition, docPointerPosition),
      color: AdaptiveColor.BLUE,
      colorMode: prefs.colorMode,
      headType: ArrowType.TRIANGLE
    };
  }

  function updateSelectDrag() {
    if (docPointerDownPosition === undefined || docPointerPosition === undefined) {
      return;
    }
    docSelectBounds = rect([docPointerDownPosition, docPointerPosition]);
    let intersectingElements = findElementsIntersectingRect(
      doc,
      docSelectBounds,
      expectDefined(spatialIndex)
    );
    const nodesToRemove: Nodes = new Set();
    // Remove groups we're completely inside of.
    for (const node of intersectingElements.nodes) {
      if (doc.isGroup(node)) {
        if (doc.nodeBounds(node).contains(docSelectBounds)) {
          nodesToRemove.add(node);
        }
      }
    }

    if (nodesToRemove.size > 0) {
      intersectingElements = intersectingElements.deletingNodes(nodesToRemove);
    }
    if (shiftAtStartDrag) {
      const newSelectedElements = expectDefined(
        elementsSelectedAtStartDrag
      ).symmetricDifference(intersectingElements);
      selection = newSelectedElements;
    } else {
      selection = intersectingElements;
    }
  }

  function updateArrangeDrag() {
    if (docPointerDownPosition === undefined || docPointerPosition === undefined) {
      return;
    }
    const line = lineSegment(docPointerDownPosition, docPointerPosition);
    const isRedirect = endpointHoveredAtStartDrag !== undefined;
    const headType = isRedirect ? ArrowType.OPEN_CIRCLE : ArrowType.OPEN_SQUARE;
    let canArrange = Commands.arrangeDryRun(
      expectDefined(docPointerDownPosition),
      expectDefined(docPointerPosition),
      hovered,
      commandContext()
    );
    const color = canArrange ? AdaptiveColor.BLUE : AdaptiveColor.RED;
    const colorMode = prefs.colorMode;
    docConnectArrow = { line, color, colorMode, headType };
  }

  function updatePanDrag(elemCurrentPointerPosition: Point) {
    if (elemLastPointerDownPosition === undefined) {
      return;
    }
    const delta = elemCurrentPointerPosition.sub(elemLastPointerDownPosition);
    pan = pan.add(delta);
  }

  function finishDrag() {
    if (!isDragging()) {
      return;
    }
    switch (dragType) {
      case DragType.CONNECT:
        {
          assertDefined(docPointerDownPosition);
          assertDefined(docPointerPosition);
          assertDefined(elementsHoveredAtStartDrag);
          assertDefined(spatialIndex);
          let startGroup = findHoveredGroupAt(doc, docPointerDownPosition, spatialIndex);
          let endGroup = findHoveredGroupAt(doc, docPointerPosition, spatialIndex);

          Commands.connect(
            docPointerDownPosition,
            docPointerPosition,
            elementsHoveredAtStartDrag,
            hovered,
            startGroup,
            endGroup,
            selectedUnitClass,
            commandContext(true)
          );
        }
        break;
      case DragType.SELECT:
        // Do nothing
        break;
      case DragType.ARRANGE:
        {
          const hoveredNode = hovered.anyNode;
          const hoveredLeafNode =
            hoveredNode !== undefined && !doc.isGroup(hoveredNode)
              ? hoveredNode
              : undefined;
          if (endpointHoveredAtStartDrag !== undefined && hoveredLeafNode !== undefined) {
            // const edges: Edges = new Set([endpointHoveredAtStartDrag.edge]).union(selection.edges);
            const edges = selection.edges;
            const direction = endpointHoveredAtStartDrag.direction;
            const destinationNode = hoveredLeafNode;
            Commands.redirect(edges, direction, destinationNode, commandContext(true));
          } else {
            Commands.arrange(
              expectDefined(docPointerDownPosition),
              expectDefined(docPointerPosition),
              hovered,
              commandContext(true)
            );
          }
        }
        break;
      case DragType.PAN:
        startInertialScrolling();
        break;
    }
    endDrag();
  }

  function endDrag() {
    if (!isDragging()) {
      return;
    }
    dragType = undefined;

    // Required to prevent another drag from starting during a pointer move
    // before the pointer goes up.
    isPointerDown = false;
    docSelectBounds = undefined;
    docConnectArrow = undefined;
    elemPointerDownPosition = undefined;
    elementsSelectedAtStartDrag = undefined;
    elementsHoveredAtStartDrag = undefined;
    endpointHoveredAtStartDrag = undefined;

    updateCursor('endDrag');
    stopAutoscroll();
    requestFrame('endDrag');
  }

  // #endregion

  // #region Click

  function handleHoveredSelectionClicked() {
    if (keyManager.shift) {
      selection = selection.inverting(hovered);
    } else {
      selection = hovered;
    }
  }

  function isClick(): boolean {
    return (
      dragType === undefined &&
      elemPointerDownPosition !== undefined &&
      elemPointerUpPosition !== undefined &&
      elemPointerUpPosition.sub(elemPointerDownPosition).magnitude < CLICK_THRESHOLD
    );
  }

  function handleClick() {
    switch (toolManager.activeTool) {
      case CommandId.TOOL_CREATE:
        connectClick();
        break;
      case CommandId.TOOL_PAN:
        // Do nothing
        break;
      case CommandId.TOOL_SELECT:
        handleHoveredSelectionClicked();
        break;
      case CommandId.TOOL_ARRANGE:
        handleHoveredSelectionClicked();
        break;
    }
  }

  function connectClick() {
    // If clicked on empty canvas or a group
    if (hovered.isEmpty) {
      // Clicked on empty canvas
      Commands.createUnit(
        selectedUnitClass,
        docPointerPosition,
        undefined,
        keyManager.shift,
        commandContext(true)
      );
    } else {
      // Clicked on an element
      const hoveredNode = hovered.anyNode;
      if (hoveredNode !== undefined && doc.isGroup(hoveredNode)) {
        // Clicked on a group
        Commands.createUnit(
          selectedUnitClass,
          docPointerPosition,
          hoveredNode,
          keyManager.shift,
          commandContext(true)
        );
      } else {
        const hoveredEdge = hovered.anyEdge;
        if (hoveredEdge !== undefined && !keyManager.shift) {
          let newNodeParent: NID | undefined;
          if (docPointerPosition !== undefined) {
            newNodeParent = findGroupAt(doc, docPointerPosition, spatialIndex);
          }
          Commands.insertUnit(
            selectedUnitClass,
            docPointerPosition,
            hoveredEdge,
            newNodeParent,
            commandContext(true)
          );
        } else {
          handleHoveredSelectionClicked();
        }
      }
    }
  }

  // #endregion

  // #region Context Menu

  // Prevent the default context menu
  function handleContextMenu(event: MouseEvent) {
    // console.log('Context menu');
    event.preventDefault();
  }

  // #endregion

  // #region Undo

  function getUndoState(): UndoState {
    return { doc, selection };
  }

  function setUndoState(state: UndoState) {
    doc = state.doc;
    selection = state.selection;
    // Elements may have been removed, so the spatial index is no longer valid.
    // The end of the transition will recreate the spatial index.
    spatialIndex = undefined;
    requestFrame('setUndoState');

    let selectionBounds = doc.elementsBounds(selection);
    if (selectionBounds !== undefined) {
      Commands.revealBounds(selectionBounds, commandContext(true));
    }
  }

  // #endregion

  // #region Wheel

  // Two-finger drag on trackpad and pinch-to-zoom
  // Zoom also works on a trackpad with ctrl+two-finger-drag
  function handleWheel(event: WheelEvent) {
    event.preventDefault();

    if (isAnimating) {
      return;
    }

    stopInertialScrolling();
    setNeedsUpdateCursor('handleWheel');

    elemPointerPosition = point(event.clientX, event.clientY);

    if (event.ctrlKey) {
      const userSensitivity = 1; // This is a preference the user could set.
      const sensitivityFactor = -0.005;
      let zoomDelta = userSensitivity * sensitivityFactor * event.deltaY;
      const oldZoom = zoom;
      const newZoom = clamp(zoom + zoomDelta, [minZoom, maxZoom]);

      const elemBounds = expectDefined(getElementBounds(element, false));
      const elemPoint = point(event.clientX, event.clientY).sub(elemBounds.topLeft);
      const docPoint = elemPoint.sub(pan).div(oldZoom);

      // Update pan to keep the point under the mouse stable
      pan = elemPoint.sub(docPoint.mul(newZoom));

      zoom = newZoom;

      requestFrame('wheel zoom');
    } else {
      const elemDelta = point(event.deltaX, event.deltaY).mul(prefs.trackpadScrollSpeed);
      pan = pan.sub(elemDelta);
    }
  }

  // #endregion

  // #region Touch

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleTouchStart(event: TouchEvent) {
    // console.log('Touch start');
    // if (event.touches.length === 2) {
    //   initialTouchPositions = Array.from(event.touches).map(touch => point(touch.clientX, touch.clientY));
    // }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleTouchMove(event: TouchEvent) {
    // console.log('Touch move');
    // if (event.touches.length === 2 && initialTouchPositions.length === 2) {
    //   const currentTouchPositions = Array.from(event.touches).map(touch => point(touch.clientX, touch.clientY));
    //   const deltaX = (currentTouchPositions[0].x - initialTouchPositions[0].x + currentTouchPositions[1].x - initialTouchPositions[1].x) / 2;
    //   const deltaY = (currentTouchPositions[0].y - initialTouchPositions[0].y + currentTouchPositions[1].y - initialTouchPositions[1].y) / 2;
    //   // Handle the two-finger drag logic here
    //   pan.x += deltaX / zoom;
    //   pan.y += deltaY / zoom;
    //   initialTouchPositions = currentTouchPositions;
    //   requestFrame();
    // }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleTouchEnd(event: TouchEvent) {
    // console.log('Touch end');
    // if (event.touches.length < 2) {
    //   initialTouchPositions = [];
    // }
  }

  // #endregion

  // #region Cancel

  function cancelName(): StringId {
    if (canCancelEditor()) {
      return StringId.CANCEL_EDITOR_COMMAND;
    } else if (canCancelDrag()) {
      return StringId.CANCEL_DRAG_COMMAND;
    } else if (canCancelSelection()) {
      return StringId.DESELECT_ALL_COMMAND;
    } else {
      return StringId.CANCEL_COMMAND;
    }
  }

  function canCancel(): boolean {
    return canCancelEditor() || canCancelDrag() || canCancelSelection();
  }

  function handleCancelSignal() {
    if (cancelEditor()) {
      return;
    }

    if (cancelDrag()) {
      return;
    }

    if (cancelSelection()) {
      return;
    }
  }

  function canCancelDrag(): boolean {
    return dragType !== undefined;
  }

  function cancelDrag(): boolean {
    if (dragType === undefined) {
      return false;
    }
    selection = expectDefined(elementsSelectedAtStartDrag);
    endDrag();
    return true;
  }

  function canCancelSelection(): boolean {
    return !selection.isEmpty;
  }

  function cancelSelection(): boolean {
    if (selection.isEmpty) {
      return false;
    }

    selection = Elements.make();
    requestFrame('cancelSelection');
    return true;
  }

  // #endregion

  // #region Observers

  function addObservers() {
    element.addEventListener('pointermove', handlePointerMoveEvent);
    element.addEventListener('pointerenter', handlePointerEnterEvent);
    element.addEventListener('pointerleave', handlePointerLeaveEvent);

    element.addEventListener('pointerdown', handlePointerDownEvent);
    element.addEventListener('pointerup', handlePointerUpEvent);
    element.addEventListener('pointercancel', handlePointerCancelEvent);
    element.addEventListener('pointerout', handlePointerOutEvent);

    element.addEventListener('wheel', handleWheel);

    element.addEventListener('contextmenu', handleContextMenu);

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchEnd);
  }

  function removeObservers() {
    element.removeEventListener('pointermove', handlePointerMoveEvent);
    element.removeEventListener('pointerenter', handlePointerMoveEvent);
    element.removeEventListener('pointerleave', handlePointerLeaveEvent);

    element.removeEventListener('pointerdown', handlePointerDownEvent);
    element.removeEventListener('pointerup', handlePointerUpEvent);
    element.removeEventListener('pointercancel', handlePointerCancelEvent);
    element.removeEventListener('pointerout', handlePointerOutEvent);

    element.removeEventListener('wheel', handleWheel);

    element.removeEventListener('contextmenu', handleContextMenu);

    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchmove', handleTouchMove);
    element.removeEventListener('touchend', handleTouchEnd);
    element.removeEventListener('touchcancel', handleTouchEnd);
  }

  // #endregion

  // #region Autoscroll

  function isAutoscrolling() {
    return autoscrollIntervalId !== undefined;
  }

  function startAutoscroll() {
    if (isAutoscrolling()) {
      return;
    }
    lastAutoscrollTimestamp = performance.now();
    autoscrollIntervalId = setInterval(autoscroll, 1000 / 60) as unknown as number;
  }

  function stopAutoscroll() {
    if (!isAutoscrolling()) {
      return;
    }
    clearInterval(autoscrollIntervalId);
    autoscrollIntervalId = undefined;
    autoscrollVector = Point.ZERO;
  }

  function autoscroll() {
    const now = performance.now();
    const deltaTime = now - lastAutoscrollTimestamp;
    lastAutoscrollTimestamp = now;
    // Update pan based on autoscrollVector and deltaTime
    const panDelta = autoscrollVector.mul(deltaTime / 1000);
    if (!panDelta.isZero) {
      pan = pan.add(panDelta);
      // Keep the start and current pointer positions stable
      elemPointerDownPosition = expectDefined(elemPointerDownPosition).add(panDelta);
      handlePointerMove();
    }
  }

  function updateAutoscrollForEvent(event: PointerEvent) {
    if (!isAutoscrolling()) {
      return;
    }
    let elemBounds = expectDefined(getElementBounds(element));
    const elemPoint = point(event.clientX, event.clientY).sub(elemBounds.topLeft);
    elemBounds = elemBounds.alignedRelativeToPoint(Point.ZERO, {
      h: HAlignment.LEFT,
      v: VAlignment.TOP
    });

    const elemInnerAutoscrollBounds = elemBounds.shrinkingBy(AUTOSCROLL_THRESHOLD);
    const elemOuterAutoscrollBounds = elemBounds.expandingBy(AUTOSCROLL_THRESHOLD);

    if (elemPoint.x < elemInnerAutoscrollBounds.left) {
      autoscrollVector = autoscrollVector.withX(
        lerpMap(
          elemPoint.x,
          [elemInnerAutoscrollBounds.left, elemOuterAutoscrollBounds.left],
          [0, AUTOSCROLL_MAX_SPEED]
        )
      );
    } else if (elemPoint.x > elemInnerAutoscrollBounds.right) {
      autoscrollVector = autoscrollVector.withX(
        lerpMap(
          elemPoint.x,
          [elemInnerAutoscrollBounds.right, elemOuterAutoscrollBounds.right],
          [0, -AUTOSCROLL_MAX_SPEED]
        )
      );
    } else {
      autoscrollVector = autoscrollVector.withX(0);
    }

    if (elemPoint.y < elemInnerAutoscrollBounds.top) {
      autoscrollVector = autoscrollVector.withY(
        lerpMap(
          elemPoint.y,
          [elemInnerAutoscrollBounds.top, elemOuterAutoscrollBounds.top],
          [0, AUTOSCROLL_MAX_SPEED]
        )
      );
    } else if (elemPoint.y > elemInnerAutoscrollBounds.bottom) {
      autoscrollVector = autoscrollVector.withY(
        lerpMap(
          elemPoint.y,
          [elemInnerAutoscrollBounds.bottom, elemOuterAutoscrollBounds.bottom],
          [0, -AUTOSCROLL_MAX_SPEED]
        )
      );
    } else {
      autoscrollVector = autoscrollVector.withY(0);
    }
  }

  // #endregion

  // #region Inertial Scroll

  function isInertialScrolling() {
    return inertialScrollIntervalId !== undefined;
  }

  function startInertialScrolling() {
    if (isInertialScrolling()) {
      return;
    }
    if (inertialPointerVelocity.magnitude < INERTIAL_SCROLL_START_VELOCITY) {
      return;
    }
    // console.log(
    //   `startInertialScrolling with velocity: ${fixedNumber(inertialPointerVelocity.magnitude, 1)}`
    // );
    lastInertialScrollTimestamp = performance.now();
    inertialScrollVector = inertialPointerVelocity;
    inertialScrollIntervalId = setInterval(
      inertialScroll,
      1000 / 60
    ) as unknown as number;
  }

  function stopInertialScrolling() {
    if (!isInertialScrolling()) {
      return;
    }
    clearInterval(inertialScrollIntervalId);
    inertialScrollIntervalId = undefined;
    inertiaPoints = [];
    inertialPointerVelocity = Point.ZERO;
    inertialScrollVector = Point.ZERO;
  }

  function inertialScroll() {
    const now = performance.now();
    const deltaTime = now - lastInertialScrollTimestamp;

    const dt = deltaTime;
    const dtSeconds = dt / 1000;
    inertialScrollVector = inertialScrollVector.mul(INERTIAL_SCROLL_DECAY ** dtSeconds);
    if (inertialScrollVector.magnitude < INERTIAL_SCROLL_END_VELOCITY) {
      stopInertialScrolling();
      return;
    }

    const panDelta = inertialScrollVector.mul(dt * prefs.panInertialScrollSensitivity);
    pan = pan.add(panDelta);

    lastInertialScrollTimestamp = now;
    requestFrame('updateInertialScroll');
  }

  function updateInertialPointerVelocity(p: Point) {
    const now = performance.now();
    // Remove points older than 100ms
    inertiaPoints = inertiaPoints.filter(([, timestamp]) => now - timestamp < 100);
    // Add the new point
    inertiaPoints.push([p, now]);
    // If we have less than 2 points, we can't calculate velocity
    const n = inertiaPoints.length;
    if (n < 2) {
      inertialPointerVelocity = Point.ZERO;
      return;
    }

    // Calculate the velocity using linear regression
    let sumT = 0,
      sumX = 0,
      sumY = 0,
      sumTT = 0,
      sumTX = 0,
      sumTY = 0;

    for (const [point, time] of inertiaPoints) {
      sumT += time;
      sumX += point.x;
      sumY += point.y;
      sumTT += time * time;
      sumTX += time * point.x;
      sumTY += time * point.y;
    }

    const denominator = n * sumTT - sumT * sumT;

    if (denominator === 0) {
      inertialPointerVelocity = Point.ZERO;
      return;
    }

    const vx = (n * sumTX - sumT * sumX) / denominator;
    const vy = (n * sumTY - sumT * sumY) / denominator;

    inertialPointerVelocity = point(vx, vy);
    // console.log(`Pointer velocity: ${inertialPointerVelocity.toString(1)}`);
  }

  // #endregion

  // #region Keybindings

  // node_modules/keyboardjs/locales/us.js

  function commandContext(commitOnUpdate = false): CommandContext {
    const args = {
      doc,
      selection,
      entryPoints: undefined,
      zoom,
      pan,
      hovered,
      undoManager,
      layouter: expectDefined(layouter),
      elemBounds: expectDefined(getElementBounds(element, false)),
      minZoom,
      maxZoom,
      commit: commitCommandState,
      commitOnUpdate
    };
    return new CommandContext(args);
  }

  function commitCommandState(result?: Partial<CommandState>) {
    if (!result) {
      return;
    }

    if (result.doc) {
      doc = result.doc;
    }

    if (result.selection) {
      selection = result.selection;
    }

    entryPoints = result.entryPoints;

    if (result.pan) {
      targetPan = result.pan;
    }

    if (result.zoom !== undefined) {
      targetZoom = result.zoom;
    }
  }

  function bindCommands() {
    // Escape
    commandManager.bind(CommandId.CANCEL, handleCancelSignal, {
      disabled: () => !canCancel(),
      nameOverride: cancelName
    });

    // View
    commandManager.bind(CommandId.ZOOM_TO_FIT, () => {
      const context = commandContext();
      Commands.zoomToFit(context);
      if (context.isUpdated && elemPointerPosition) {
        updateCursor('zoomToFit');
      }
      context.commit();
    });
    commandManager.bind(CommandId.ZOOM_100_PERCENT, () => {
      Commands.zoom(1, elemPointerPosition, commandContext(true));
    });
    commandManager.bind(CommandId.ZOOM_200_PERCENT, () => {
      Commands.zoom(2, elemPointerPosition, commandContext(true));
    });
    commandManager.bind(CommandId.CENTER_ON_POINT, () => {
      Commands.centerOnPoint(docPointerPosition, commandContext(true));
    });
    commandManager.bind(CommandId.FOCUS_SELECTION, () => {
      Commands.focusSelection(commandContext(true));
    });

    // Platform and Environment
    commandManager.bind(CommandId.TOGGLE_COLOR_MODE, () => {
      prefs.toggleColorMode();
    });
    commandManager.bind(CommandId.NEXT_LOCALE, () => {
      localeManager.nextLocale();
    });
    commandManager.bind(CommandId.PREV_LOCALE, () => {
      localeManager.prevLocale();
    });

    // Undo/Redo
    commandManager.bind(
      CommandId.UNDO,
      () => {
        Commands.undo(commandContext(true));
      },
      {
        disabled: () => !Commands.canUndo(commandContext())
      }
    );
    commandManager.bind(
      CommandId.REDO,
      () => {
        Commands.redo(commandContext(true));
      },
      {
        disabled: () => !Commands.canRedo(commandContext())
      }
    );

    // Delete
    commandManager.bind(CommandId.DELETE_SELECTION, () => {
      const context = commandContext();
      const result = Commands.deleteSelection(context);
      if (result !== undefined) {
        // Elements were deleted, so we need to make sure that the deleted
        // elements are not in the spatial index. If the hover state is updated
        // from this index, it may reference deleted elements. Since we're going
        // to do transition animation, we don't have to rebuild it here, as it
        // will be rebuilt at the end of the transition.
        spatialIndex = undefined;
      }
      context.commit();
    });

    // Group
    commandManager.bind(CommandId.GROUP_SELECTION, () => {
      Commands.groupSelection(selectedGroupClass, commandContext(true));
    });

    // Select All
    commandManager.bind(CommandId.SELECT_ALL, () => {
      Commands.selectAll(commandContext(true));
    });

    // Select Successors
    commandManager.bind(CommandId.SELECT_SUCCESSORS, () => {
      Commands.selectSuccessors(commandContext(true));
    });

    // Select Predessors
    commandManager.bind(CommandId.SELECT_PREDECESSORS, () => {
      Commands.selectPredecessors(commandContext(true));
    });

    // Select Neighbors
    commandManager.bind(CommandId.SELECT_NEIGHBORS, () => {
      Commands.selectNeighbors(commandContext(true));
    });

    // Select All Successors
    commandManager.bind(CommandId.SELECT_ALL_SUCCESSORS, () => {
      Commands.selectAllSuccessors(commandContext(true));
    });

    // Select All Predecessors
    commandManager.bind(CommandId.SELECT_ALL_PREDECESSORS, () => {
      Commands.selectAllPredecessors(commandContext(true));
    });

    // Select All Neighbors
    commandManager.bind(CommandId.SELECT_ALL_NEIGHBORS, () => {
      Commands.selectAllNeighbors(commandContext(true));
    });

    // Collapse Selected Groups
    commandManager.bind(CommandId.COLLAPSE_GROUPS, () => {
      Commands.collapseGroups(commandContext(true));
    });

    // Deep Collapse Selected Groups
    commandManager.bind(CommandId.DEEP_COLLAPSE_GROUPS, () => {
      Commands.deepCollapseGroups(commandContext(true));
    });

    // Expand Selected Groups
    commandManager.bind(CommandId.EXPAND_GROUPS, () => {
      Commands.expandGroups(commandContext(true));
    });

    // Deep Expand Selected Groups
    commandManager.bind(CommandId.DEEP_EXPAND_GROUPS, () => {
      Commands.deepExpandGroups(commandContext(true));
    });

    // Reverse Selected edges
    commandManager.bind(CommandId.REVERSE_EDGES, () => {
      Commands.reverseEdges(selection.edges, commandContext(true));
    });

    // Reload Window
    commandManager.bind(CommandId.RELOAD_WINDOW, () => {
      window.location.reload();
    });

    // Tools
    commandManager.bind(
      CommandId.TOOL_CREATE,
      () => toolManager.startSelectTool(CommandId.TOOL_CREATE),
      { keyUpHandler: () => toolManager.endSelectTool() }
    );

    commandManager.bind(
      CommandId.TOOL_SELECT,
      () => toolManager.startSelectTool(CommandId.TOOL_SELECT),
      { keyUpHandler: () => toolManager.endSelectTool() }
    );

    commandManager.bind(
      CommandId.TOOL_ARRANGE,
      () => toolManager.startSelectTool(CommandId.TOOL_ARRANGE),
      { keyUpHandler: () => toolManager.endSelectTool() }
    );

    commandManager.bind(
      CommandId.TOOL_PAN,
      () => toolManager.startSelectTool(CommandId.TOOL_PAN),
      { keyUpHandler: () => toolManager.endSelectTool() }
    );

    commandManager.bind(CommandId.TOGGLE_BOTTOM_BAR, () => {
      prefs.bottomBarVisible = !prefs.bottomBarVisible;
    });

    commandManager.bind(
      CommandId.SHOW_EDITOR,
      () => {
        if (canRevealSelection()) {
          revealSelection();
        } else if (canDeselectEdges()) {
          deselectEdges();
        } else {
          toggleEditor();
        }
      },
      {
        disabled: () => {
          return !canRevealSelection() && !canShowEditor() && !canDeselectEdges();
        },
        nameOverride: () => {
          if (canRevealSelection()) {
            return StringId.REVEAL_SELECTION_COMMAND;
          } else if (canDeselectEdges()) {
            return StringId.DESELECT_EDGES_COMMAND;
          } else {
            return StringId.SHOW_EDITOR_COMMAND;
          }
        }
      }
    );

    commandManager.bind(CommandId.LAYOUT_LEFT_TO_RIGHT, () => {
      Commands.setOrientation(LayoutOrientation.LEFT_TO_RIGHT, commandContext(true));
    });

    commandManager.bind(CommandId.LAYOUT_RIGHT_TO_LEFT, () => {
      Commands.setOrientation(LayoutOrientation.RIGHT_TO_LEFT, commandContext(true));
    });

    commandManager.bind(CommandId.LAYOUT_TOP_TO_BOTTOM, () => {
      Commands.setOrientation(LayoutOrientation.TOP_TO_BOTTOM, commandContext(true));
    });

    commandManager.bind(CommandId.LAYOUT_BOTTOM_TO_TOP, () => {
      Commands.setOrientation(LayoutOrientation.BOTTOM_TO_TOP, commandContext(true));
    });

    commandManager.bind(CommandId.LAYOUT_UNCONSTRAINED, () => {
      Commands.setOrientation(LayoutOrientation.UNCONSTRAINED, commandContext(true));
    });

    commandManager.bind(CommandId.TOGGLE_BUNDLED_ROUTING, () => {
      Commands.toggleBundledRouting(commandContext(true));
    });

    commandManager.bind(CommandId.TOGGLE_ALIGN_ROOTS, () => {
      Commands.toggleAlignRoots(commandContext(true));
    });

    commandManager.bind(CommandId.TOGGLE_ALIGN_LEAVES, () => {
      Commands.toggleAlignLeaves(commandContext(true));
    });

    commandManager.bind(CommandId.SET_SELECTED_UNITS_CLASS, () => {
      Commands.setSelectedUnitsClass(selectedUnitClass, commandContext(true));
    });

    commandManager.logCommandKeys();
  }

  // #endregion

  // #region Editor

  function canRevealSelection(): boolean {
    let docSelectionBounds = doc.elementsBounds(selection);
    if (docSelectionBounds === undefined) {
      return false;
    }
    let elemSelectionBounds = toElemPosition(docSelectionBounds);
    if (elemSelectionBounds === undefined) {
      return false;
    }
    const htmlElementBounds = expectDefined(getElementBounds(element, true));
    return !htmlElementBounds.contains(elemSelectionBounds);
  }

  function revealSelection() {
    let docSelectionBounds = doc.elementsBounds(selection);
    if (docSelectionBounds === undefined) {
      return;
    }
    Commands.revealBounds(docSelectionBounds, commandContext(true));
  }

  function canDeselectEdges(): boolean {
    return selection.nodes.size === 1 && selection.edges.size > 0;
  }

  function deselectEdges() {
    selection = selection.clearingEdges();
  }

  function canShowEditor() {
    return !isEditorVisible() && selection.nodes.size === 1;
  }

  function showEditor() {
    if (!canShowEditor()) {
      return;
    }
    const selectedNode = expectDefined(selection.anyNode);
    titleEditorState.node = selectedNode;
    titleEditorState.title = doc.nodeTitle(selectedNode).get();
    const editorSize = size(200, 150);
    const docNodeBounds = doc.nodeBounds(selectedNode);
    const elemNodeBounds = expectDefined(toElemPosition(docNodeBounds, false))
      .settingWidth(editorSize.width)
      .settingHeight(editorSize.height)
      .expandingBy(5);
    const elemBounds = expectDefined(getElementBounds(element, false));
    const padding = LayoutConstants.PAN_ZOOM_REVEAL_PADDING;
    const elemViewportRect = elemBounds.shrinkingBy(padding);
    titleEditorState.editorBounds = elemNodeBounds.revealWithinRect(elemViewportRect);

    titleEditorState.isVisible = true;
  }

  function canHideEditor(): boolean {
    return isEditorVisible();
  }

  function canCancelEditor(): boolean {
    return canHideEditor();
  }

  function hideEditor(): boolean {
    if (!canHideEditor()) {
      return false;
    }

    titleEditorState.isVisible = false;
    return true;
  }

  function cancelEditor(): boolean {
    if (!canCancelEditor()) {
      return false;
    }
    return hideEditor();
  }

  function toggleEditor() {
    if (isEditorVisible()) {
      hideEditor();
    } else {
      showEditor();
    }
  }

  function isEditorVisible(): boolean {
    return titleEditorState.isVisible;
  }

  // When the editor registers a change, update the title of the node.
  $effect(() => {
    if (titleEditorState.change) {
      untrack(() => {
        assertDefined(titleEditorState.node);
        let title = trimLines(titleEditorState.title);
        if (title === '') {
          title = doc.firstAvailableLetterCode();
        } else {
          // Handle `_lo [wordCount] [locale]`
          title = loremify(title);
        }
        Commands.setNodeTitle(
          titleEditorState.node,
          iString(title),
          commandContext(true)
        );
      });
    }
  });

  // When the editor is hidden, we want to blur the active element,
  // so that the focus is not on the editor when it's not visible.
  $effect(() => {
    if (!titleEditorState.isVisible) {
      (document.activeElement as HTMLElement).blur();
    }
  });

  // #endregion
</script>

<!--
We don't use Svelte bindings because they can result
in early calls to the event handlers, which can cause
errors if the component is not yet fully set up.
on:pointermove={handlePointerMove}
on:pointerenter={handlePointerEnter}
on:pointerleave={handlePointerLeave}
-->

<!-- #region Style
-->

<style>
  .canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    touch-action: none;
    pointer-events: all;
    cursor: var(--cursor-style, default);
  }
</style>

<!-- #region HTML
-->

<canvas bind:this={element} id="DocCanvas" class="canvas"></canvas>
